import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import './CompactHeader.css';

interface CompactHeaderProps {
  reportTitle: string;
}

/**
 * CompactHeader component - displays a compact header for report pages
 */
const CompactHeader: React.FC<CompactHeaderProps> = ({ reportTitle }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };
  
  return (
    <header className="compact-header">
      <div className="header-container">
        <div className="header-left">
          <Link to="/reports" className="home-link">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <span className="report-title">{reportTitle}</span>
        </div>
        
        <div className="header-right">
          <Link to="/profile" className="profile-link">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          
          <div className="menu-container" ref={menuRef}>
            <button className="menu-button" onClick={toggleMenu}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {menuOpen && (
              <div className="dropdown-menu">
                <div className="menu-item" onClick={() => handleMenuItemClick('/reports')}>
                  {t('nav.my_reports')}
                </div>
                <div className="menu-item" onClick={() => handleMenuItemClick('/create-report')}>
                  {t('nav.create_report')}
                </div>
                <div className="menu-item" onClick={() => handleMenuItemClick('/profile')}>
                  {t('user_menu.profile')}
                </div>
                <div className="menu-item" onClick={() => handleMenuItemClick('/settings')}>
                  {t('compact_header.settings')}
                </div>
                <div className="menu-item" onClick={() => handleMenuItemClick('/help')}>
                  {t('compact_header.help')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default CompactHeader;
